/** Saved-view management UI. The authenticated Rust routes enforce ownership and consent.
 * @module apps/web/src/components/flux-agent/views/FluxViewsDialog
 */
import { useEffect, useRef, useState, type ComponentProps } from 'react';
import { useUserStore } from '@/stores/userStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { fluxViewsApi, type FluxView } from '@/lib/fluxViewsApi';
import { FluxViewOpener } from './FluxViewOpener';

/** Keep retained view-list presentation state isolated to the current account. */
export function FluxViewsDialog(props: ComponentProps<typeof OwnerFluxViewsDialog>) {
  const owner = useUserStore(state => state.userId);
  return <OwnerFluxViewsDialog key={owner ?? 'signed-out'} {...props} />;
}

/** List and manage one account's views, retaining successful results during refresh. */
function OwnerFluxViewsDialog({
  open,
  onOpenChange,
  onSuggest,
}: {
  /** Whether the dialog is visible. */
  open: boolean;
  /** Close or reopen the dialog. */
  onOpenChange: (open: boolean) => void;
  /** Put a user-selected suggestion in the composer. */
  onSuggest?: (text: string) => void;
}) {
  const [views, setViews] = useState<FluxView[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // A mutation invalidates any older list reply so it cannot restore deleted or revoked rows.
  const requestVersion = useRef(0);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [deleting, setDeleting] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    let live = true;
    const version = ++requestVersion.current;
    setLoading(true);
    setError('');
    setSelected(null);
    setDeleting(null);
    setEditing(null);
    void fluxViewsApi.listViews().then(result => {
      if (!live || version !== requestVersion.current) return;
      setLoading(false);
      if (result.ok) {
        setViews(result.data.views);
        setLoaded(true);
      } else setError(result.message);
    });
    return () => {
      live = false;
    };
  }, [open]);
  const rename = async (view: FluxView) => {
    ++requestVersion.current;
    setLoading(false);
    setBusy(true);
    setError('');
    const result = await fluxViewsApi.renameView(view.id, title);
    ++requestVersion.current;
    setLoading(false);
    setBusy(false);
    if (!result.ok) return setError(result.message);
    setViews(current => current.map(item => (item.id === view.id ? result.data.view : item)));
    setEditing(null);
  };
  const revoke = async (view: FluxView) => {
    ++requestVersion.current;
    setLoading(false);
    setBusy(true);
    setError('');
    setSelected(null);
    const result = await fluxViewsApi.setAccess(view.id, view.revision, false, []);
    ++requestVersion.current;
    setLoading(false);
    setBusy(false);
    if (!result.ok) return setError(result.message);
    setViews(current => current.map(item => (item.id === view.id ? result.data.view : item)));
  };
  const remove = async (view: FluxView) => {
    ++requestVersion.current;
    setLoading(false);
    setBusy(true);
    setError('');
    const result = await fluxViewsApi.deleteView(view.id);
    ++requestVersion.current;
    setLoading(false);
    setBusy(false);
    if (!result.ok) return setError(result.message);
    setViews(current => current.filter(item => item.id !== view.id));
    setDeleting(null);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Your views</DialogTitle>
          <DialogDescription>
            Open a view Flux Agent made, or manage the data it may read.
          </DialogDescription>
        </DialogHeader>
        {error && (
          <p role="alert" className="text-destructive text-sm">
            {error}
          </p>
        )}
        {loading && loaded && <p role="status">Refreshing views…</p>}
        {loading && !loaded ? (
          <p role="status">Loading views…</p>
        ) : selected ? (
          <div className="space-y-3">
            <Button variant="ghost" onClick={() => setSelected(null)}>
              Back to views
            </Button>
            <FluxViewOpener
              key={selected}
              viewId={selected}
              onSuggest={text => {
                onOpenChange(false);
                onSuggest?.(text);
              }}
            />
          </div>
        ) : (
          <ul className="space-y-3" aria-label="Saved views">
            {!views.length && !error && (
              <li className="text-muted-foreground text-sm">
                Ask Flux Agent to make a tracker, chart or dashboard. Your views will appear here.
              </li>
            )}
            {views.map(view => (
              <li key={view.id} className="space-y-2 rounded-md border p-3">
                {editing === view.id ? (
                  <form
                    className="flex gap-2"
                    onSubmit={event => {
                      event.preventDefault();
                      void rename(view);
                    }}
                  >
                    <Input
                      aria-label="View title"
                      value={title}
                      maxLength={80}
                      onChange={event => setTitle(event.target.value)}
                    />
                    <Button size="sm" disabled={busy}>
                      Save title
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      disabled={busy}
                      onClick={() => setEditing(null)}
                    >
                      Cancel
                    </Button>
                  </form>
                ) : (
                  <>
                    <p className="font-medium">{view.title}</p>
                    <p className="text-muted-foreground text-sm">{view.purpose}</p>
                  </>
                )}
                <p className="text-muted-foreground text-xs">
                  {view.access.needed
                    ? view.access.granted
                      ? 'Data access allowed'
                      : 'Data access not allowed'
                    : 'No personal data requested'}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => setSelected(view.id)} disabled={busy}>
                    Open
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => {
                      setEditing(view.id);
                      setTitle(view.title);
                    }}
                  >
                    Rename
                  </Button>
                  {view.access.needed && view.access.granted && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => void revoke(view)}
                    >
                      Revoke data access
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => setDeleting(view.id)}
                  >
                    Delete
                  </Button>
                </div>
                {deleting === view.id && (
                  <div className="border-destructive/40 space-y-2 rounded border p-2">
                    <p className="text-sm">Delete “{view.title}”? This removes the saved view.</p>
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={busy}
                      onClick={() => void remove(view)}
                    >
                      Confirm delete
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy}
                      onClick={() => setDeleting(null)}
                    >
                      Cancel deletion
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
